import { useCallback } from 'react'

import track from 'utils/analytics'

// ----------------------------------------------------------------------
// Canonical analytics names from the shared cross-platform taxonomy
// (DOF-1069): dofarm_obsidian_vault/conventions/analytics-tracking-plan.md
// Pilot scope: login only — grow these alongside the taxonomy doc, never
// invent ad-hoc event names at call sites.

export const AnalyticsEvents = {
  // GA4 recommended event — fires on confirmed login success, never on click
  login: 'login',
  signUpOtpRequested: 'sign_up_otp_requested',
  signUpOtpVerified: 'sign_up_otp_verified',
  signUp: 'sign_up',
  loginOtpRequested: 'login_otp_requested',
  logout: 'logout',
  passwordResetRequested: 'password_reset_requested',
  passwordResetCompleted: 'password_reset_completed',
  // DOF-1175: farm lifecycle — see conventions/analytics-tracking-plan.md.
  farmCreated: 'farm_created',
  farmUpdated: 'farm_updated',
  farmDeleted: 'farm_deleted',
  // DOF-1176: pond lifecycle — see conventions/analytics-tracking-plan.md.
  pondCreated: 'pond_created',
  pondUpdated: 'pond_updated',
  pondDeleted: 'pond_deleted',
  pondPinned: 'pond_pinned',
  // DOF-1177: cycle lifecycle — activation milestone.
  cycleStarted: 'cycle_started',
  cycleUpdated: 'cycle_updated',
  cycleCompleted: 'cycle_completed',
  cycleDeleted: 'cycle_deleted',
  // DOF-1178: feed record lifecycle.
  feedRecordCreated: 'feed_record_created',
  feedRecordUpdated: 'feed_record_updated',
  feedRecordDeleted: 'feed_record_deleted',
  // DOF-1181: medicine record lifecycle.
  medicineRecordCreated: 'medicine_record_created',
  medicineRecordUpdated: 'medicine_record_updated',
  medicineRecordDeleted: 'medicine_record_deleted',
  // DOF-1182: growth record lifecycle.
  growthRecordCreated: 'growth_record_created',
  growthRecordUpdated: 'growth_record_updated',
  growthRecordDeleted: 'growth_record_deleted',
  // DOF-1183: mortality record lifecycle.
  mortalityRecordCreated: 'mortality_record_created',
  mortalityRecordUpdated: 'mortality_record_updated',
  mortalityRecordDeleted: 'mortality_record_deleted',
  // DOF-1179: water quality record lifecycle — one event name shared by all
  // 3 sub-types (quality/mineral/vibrio), differentiated only by the
  // water_record_type param.
  waterQualityRecordCreated: 'water_quality_record_created',
  waterQualityRecordUpdated: 'water_quality_record_updated',
  waterQualityRecordDeleted: 'water_quality_record_deleted',
  // DOF-1180: cost record lifecycle — see conventions/analytics-tracking-plan.md.
  costRecordCreated: 'cost_record_created',
  costRecordUpdated: 'cost_record_updated',
  costRecordDeleted: 'cost_record_deleted',
  // Home dashboard banner/announcement impressions — mirrors mobile's
  // AnalyticsEvents.bannerImpression. Web currently only has the system
  // announcement popup (no carousel/ads banners).
  bannerImpression: 'banner_impression',
  // DOF-1184: shrimp sale lifecycle — see conventions/analytics-tracking-plan.md.
  salesRecordCreated: 'sales_record_created',
  salesRecordUpdated: 'sales_record_updated',
  salesRecordDeleted: 'sales_record_deleted',
  // DOF-1185: shrimp transfer lifecycle — a transfer affects 2 ponds (source +
  // destination) in one backend call; fire exactly once per transfer, never
  // once per pond. See conventions/analytics-tracking-plan.md.
  transferRecordCreated: 'transfer_record_created',
  transferRecordUpdated: 'transfer_record_updated',
  transferRecordDeleted: 'transfer_record_deleted',
  // DOF-1186: shrimp stocking ("topup") lifecycle — distinct from
  // credit_topup_* (real-money wallet top-up, subtask 24) despite the shared
  // "topup" word. See conventions/analytics-tracking-plan.md.
  stockingRecordCreated: 'stocking_record_created',
  stockingRecordUpdated: 'stocking_record_updated',
  stockingRecordDeleted: 'stocking_record_deleted',
  // DOF-1187: pond comparison — fires when the comparison TABLE result
  // arrives, never on button click and never on page entry.
  pondComparisonRun: 'pond_comparison_run',
  // DOF-1188: custom product creation — shared across the feed and medicine
  // custom-product entry points. No single shared mutation exists; each
  // entry point fires this independently.
  productCreated: 'product_created',
  // DOF-1189: IoT device registration succeeded — fires on both platforms
  // (mobile also ships a full registration flow, despite the original
  // taxonomy assuming it was view-only). No params.
  iotDeviceRegistered: 'iot_device_registered',
  // DOF-1197: org dashboard (screen_view stays `organization_overview` —
  // pre-existing, see AnalyticsScreens on mobile; this ticket only adds
  // interaction events). One event per interaction type across all 5
  // sections, never split per section.
  orgDashboardSectionSettingsUpdated: 'org_dashboard_section_settings_updated',
  orgDashboardFilterApplied: 'org_dashboard_filter_applied',
  orgDashboardPondDrilldownOpened: 'org_dashboard_pond_drilldown_opened',
  // DOF-1196: credit wallet — real-money monetization funnel. No
  // creditWalletViewed event: screen_view(credit_wallet) already fires on
  // every visit (Page.tsx route-based auto-tracking), a second custom event
  // would duplicate that signal. Never log receipts, purchase/verification
  // tokens, order ids, or promo codes at any of these call sites — see
  // conventions/analytics-tracking-plan.md.
  creditPackagesViewed: 'credit_packages_viewed',
  creditOrderPreviewed: 'credit_order_previewed',
  creditTopupStarted: 'credit_topup_started',
  creditTopupSucceeded: 'credit_topup_succeeded',
  creditTopupFailed: 'credit_topup_failed',
  creditPromoRedeemed: 'credit_promo_redeemed',
  // DOF-1192: member/employee invited successfully. `role` uses the same
  // normalizeFarmRole() pipeline as the `farm_role` user property, for
  // cross-platform consistency.
  memberInvited: 'member_invited',
  // DOF-1194: data export succeeded (file ready/downloaded/shared) — never
  // on the export button click. See conventions/analytics-tracking-plan.md.
  dataExported: 'data_exported',
} as const

// DOF-1194: export_type — which report was exported. `format` uses the real
// ExportFormat enum values directly ('pdf'/'xlsx'/'csv') at each call site.
export const ExportType = {
  summary: 'summary',
  daily: 'daily',
  features: 'features',
} as const

// DOF-1184: sale_value/sale_currency — named to avoid GA4's reserved
// `value`/`currency` params, which GA4 auto-folds into monetary/revenue
// reporting on ANY event (not just recommended events). That would mix sale
// totals with real company revenue (credit topups). Kept descriptive-only.
export const AnalyticsCurrency = {
  thb: 'THB',
} as const

// DOF-1188: product_domain — which custom-product entry point fired.
export const ProductDomain = {
  feed: 'feed',
  medicine: 'medicine',
} as const

// DOF-1178: feed_source — 'mixed' when a single record has both product and
// natural feed (a record can be both simultaneously; not in the original
// taxonomy, agreed with the team as the value for that case).
export const FeedSource = {
  product: 'product',
  natural: 'natural',
  mixed: 'mixed',
} as const

export const feedSourceValue = (hasPellets?: boolean, hasNatural?: boolean): string | undefined => {
  if (hasPellets && hasNatural) return FeedSource.mixed
  if (hasPellets) return FeedSource.product
  if (hasNatural) return FeedSource.natural
  return undefined
}

export const AnalyticsScreens = {
  login: 'login',
} as const

// DOF-1197: org_dashboard_filter_applied / org_dashboard_pond_drilldown_opened
// `section` param — mirrors the backend's POND_STATUS/YIELD_SIZE_CURRENT/...
// section codes, lowercased for analytics.
export const OrgDashboardSection = {
  pondStatus: 'pond_status',
  yieldSizeCurrent: 'yield_size_current',
  yieldSizeEstimate: 'yield_size_estimate',
  harvest: 'harvest',
  finance: 'finance',
} as const

// DOF-1197: org_dashboard_filter_applied `filter_type`. `monthSelect` is
// deliberately not a "range" value — FINANCE only ever lets the user pick
// one point on a fixed 6-month trend chart, there is no range control.
export const OrgDashboardFilterType = {
  breed: 'breed',
  sizeRange: 'size_range',
  monthSelect: 'month_select',
} as const

// DOF-1196: credit_topup_* `store` — mirrors mobile's google/apple values;
// web only ever sends 'web'.
export const CreditStore = {
  web: 'web',
} as const

// DOF-1196: credit_topup_failed `reason` — coarse enum only, never the raw
// order/API error text.
export const CreditTopupFailedReason = {
  cancelled: 'cancelled',
  paymentError: 'payment_error',
  verifyFailed: 'verify_failed',
} as const

export const AnalyticsMethods = {
  phoneOtp: 'phone_otp',
} as const

// ----------------------------------------------------------------------

const useAnalytics = () => {
  const trackEvent = useCallback((name: string, params?: Gtag.CustomParams) => {
    track.event(name, params)
  }, [])

  const trackScreen = useCallback((screenName: string, screenClass?: string) => {
    track.event('screen_view', { screen_name: screenName, screen_class: screenClass })
  }, [])

  const identify = useCallback((userId: string | null) => {
    track.setUserId(userId)
  }, [])

  const setUserProperty = useCallback((name: string, value: string | null) => {
    track.setUserProperty(name, value)
  }, [])

  return { trackEvent, trackScreen, identify, setUserProperty }
}

export default useAnalytics
